import React, { FC, Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { List as ImmutableList } from 'immutable';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { ILeadBulkSalespersonDto, ILeadImt, TViewType } from 'modules/crm/interfaces/leads';
import { ImmutableObject } from 'common/state/interfaces';
import { ICameFromIndicatorDictionaryItemImt } from 'common/interfaces/dictionary';
import { ITableParams } from 'common/interfaces/table';
import { FilterTypes, IFilterSettings, ISingleFilterValue } from 'common/interfaces/filter';
import { IPageMetaImt } from 'common/interfaces/pagination';
import { DictionaryList, QueryPageList } from 'common/constants';
import { LeadStatuses } from 'modules/crm/constants/leads';
import { LeadsTable } from 'modules/crm/components';
import { RouteBackground } from 'components';
import { makeRequestUrlFromFilter, makeTableParams, pushQueryToUrl } from 'common/utils/http';
import { useAppDispatch } from 'store/hooks';
import * as actions from 'modules/crm/state/leads/actions';
import * as selectors from 'modules/crm/state/leads/selectors';
import { selectDictionaryList } from 'common/state/dictionary/selectors';
import { fetchDictionaryList } from 'common/state/dictionary/actions';
import { updateQueryParams } from 'common/state/queryPage-lists/actions';
import {
  selectCurrentUser,
  selectFilterByClubDefaultValue,
} from 'modules/authentication/state/selectors';
import { CustomerStatuses } from 'modules/front-desk/constants/common/constants';
import tableFiltersMessages from 'common/messages/tableFilters';
import { PeakModules } from 'common/constants/peakModules';
import Services from 'services/network';
import commonMessages from 'common/messages/messages';
import { useUpdateFiltersOnChangeClub } from 'common/hooks/useUpdateFiltersOnChangeClub';
import { makeEmployeeLabel } from 'utils/employee.utils';
import { IViewTypeData } from './LeadsContainer';
import { useLazyGetSalespersons } from 'api/dictionary.api';
import { PoliciesPermissionSetItems as Permission } from 'common/constants/permissions';
import useFirst from 'common/hooks/useFirst';
import useClubFilterOptions from 'common/hooks/useClubFilterOptions';
import useComponentDidUpdate from 'common/hooks/useComponentDidUpdate';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import { IUserProfileInfoImt } from 'modules/authentication/interfaces';
import { RouteParts } from 'constants/route.constants';

interface ILeads {
  setViewTypeData: Dispatch<SetStateAction<IViewTypeData>>;
  viewType: TViewType;
  locationSearch: string;
  viewTypeData: IViewTypeData;
  isTableInitialized: boolean;
  leadViewPermission:
    | Permission.CRM_LEAD_VIEW
    | Permission.PT_CRM_LEAD_VIEW
    | Permission.CRM_LEAD_VIEW_OWN
    | Permission.PT_CRM_LEAD_VIEW_OWN;
}

const Leads: FC<ILeads> = ({
  setViewTypeData,
  viewType,
  locationSearch,
  viewTypeData,
  isTableInitialized,
  leadViewPermission,
}) => {
  const dispatch = useAppDispatch();
  const first = useFirst();
  const location = useLocation();
  const {
    PT_CRM_LEAD_VIEW,
    PT_CRM_LEAD_VIEW_OWN,
    CRM_LEAD_VIEW,
    CRM_LEAD_VIEW_OWN,
  } = usePermissionContext();
  const navigate = useNavigate();
  const clubFilterOptions = useClubFilterOptions(leadViewPermission);
  const clubFilterName = 'clubIds';
  const [getSalespersons] = useLazyGetSalespersons();
  const profileImt: IUserProfileInfoImt = useRootSelector(selectCurrentUser);
  const profile = useMemo(() => profileImt?.toJS(), [profileImt]);

  const leads: ImmutableList<ILeadImt> = useRootSelector(selectors.selectLeads());
  const leadsMeta: IPageMetaImt = useRootSelector(selectors.selectLeadsMeta());
  const isLeadsLoading: boolean = useRootSelector(selectors.selectLeadsLoading());
  const cameFrom: ImmutableList<ICameFromIndicatorDictionaryItemImt> = useRootSelector(
    selectDictionaryList(DictionaryList.CAME_FROM),
  );

  const viewPermissions = useMemo(() => {
    if (location.pathname.includes(RouteParts.PT_CRM)) {
      return PT_CRM_LEAD_VIEW || PT_CRM_LEAD_VIEW_OWN;
    } else if (location.pathname.includes(RouteParts.CRM)) {
      return CRM_LEAD_VIEW || CRM_LEAD_VIEW_OWN;
    }

    return false;
  }, [location, PT_CRM_LEAD_VIEW, PT_CRM_LEAD_VIEW_OWN, CRM_LEAD_VIEW, CRM_LEAD_VIEW_OWN]);

  const filterByClubDefaultValue: ISingleFilterValue = useRootSelector(
    selectFilterByClubDefaultValue,
  );
  const isLeadsAssignLoading: boolean = useRootSelector(selectors.selectLeadsAssignLoading());
  const isLeadsStatusChangeLoading: boolean = useRootSelector(
    selectors.selectLeadsStatusChangeLoading(),
  );
  const leadsActionResult: ImmutableObject<Record<string, boolean>> = useRootSelector(
    selectors.selectLeadsActionResult(),
  );

  const module =
    location.pathname.indexOf('personal-training-crm') === -1
      ? PeakModules.Crm
      : PeakModules.PersonalTrainingCrm;

  const clubFilterOps = useMemo(() => {
    const filterOptions = [...clubFilterOptions];

    filterOptions.unshift({
      key: 'emptyHomeClub',
      label: <FormattedMessage {...commonMessages.emptyHomeClubLabel} />,
      value: null,
    });

    return filterOptions;
  }, [clubFilterOptions]);

  const tableFilterSettings = useMemo(
    (): IFilterSettings[] => [
      {
        name: clubFilterName,
        title: <FormattedMessage {...tableFiltersMessages.club} />,
        type: FilterTypes.MULTIPLE,
        options: clubFilterOps,
        defaultValue: filterByClubDefaultValue ? [filterByClubDefaultValue] : undefined,
      },
      {
        name: 'salesStatusSet',
        title: (
          <FormattedMessage
            {...(module === PeakModules.Crm
              ? tableFiltersMessages.crmStatus
              : tableFiltersMessages.ptCrmStatus)}
          />
        ),
        type: FilterTypes.MULTIPLE,
        options: LeadStatuses.getFilterOptions(),
      },
      {
        name: 'customerTypes',
        title: <FormattedMessage {...tableFiltersMessages.membershipStatus} />,
        type: FilterTypes.MULTIPLE,
        options: CustomerStatuses.getFilterOptions(),
      },
      {
        name: 'salespersonsIds',
        title: <FormattedMessage {...tableFiltersMessages.salesperson} />,
        type: FilterTypes.MULTIPLE_WITH_PAGINATE,
        perPage: 20,
        fetchItemTitle: async (id: number) => {
          try {
            const employee = await Services.Employees.getEmployeeById(id);
            return `${employee.firstName} ${employee.lastName}`;
          } catch (e) {
            return null;
          }
        },
        fetchList: async (search, page) => {
          try {
            const { meta, data } = await getSalespersons({
              search,
              page,
              perPage: 20,
            }).unwrap();

            return {
              pageElements: data.map(item => ({
                key: String(item.id),
                label: makeEmployeeLabel(item),
                value: item.id,
              })),
              itemCount: meta.total,
            };
          } catch (e) {
            return { pageElements: [], itemCount: 0 };
          }
        },
        defaultValue:
          CRM_LEAD_VIEW || PT_CRM_LEAD_VIEW
            ? []
            : [
                {
                  key: `${profile?.id}`,
                  label: `${profile?.firstName} ${profile?.lastName}`,
                  value: profile?.id,
                },
              ],
        hidden: CRM_LEAD_VIEW_OWN || PT_CRM_LEAD_VIEW_OWN,
      },
      {
        name: 'cameFromIndicatorIds',
        title: <FormattedMessage {...tableFiltersMessages.cameFrom} />,
        type: FilterTypes.MULTIPLE,
        options: cameFrom.toJS().map(item => ({
          key: item.id,
          label: item.title,
          value: item.id,
        })),
      },
      {
        name: 'created',
        title: <FormattedMessage {...tableFiltersMessages.created} />,
        type: FilterTypes.DATE_RANGE,
        options: {
          startDate: null,
          endDate: null,
        },
      },
    ],
    [
      clubFilterOps,
      filterByClubDefaultValue,
      module,
      CRM_LEAD_VIEW,
      PT_CRM_LEAD_VIEW,
      CRM_LEAD_VIEW_OWN,
      PT_CRM_LEAD_VIEW_OWN,
      profile,
      cameFrom,
      getSalespersons,
    ],
  );
  const [tableParams] = useUpdateFiltersOnChangeClub(
    {
      filterSettings:
        isTableInitialized && first
          ? tableFilterSettings.map(item => {
              if (clubFilterName === item.name) {
                return { ...item, defaultValue: null as any }; // TODO - PRM-3575 need types
              }

              return item;
            })
          : tableFilterSettings,
      clubFilterName,
    },
    locationSearch,
  );

  useEffect(() => {
    dispatch(fetchDictionaryList(DictionaryList.CAME_FROM, { module }));
    dispatch(fetchDictionaryList(DictionaryList.EMPLOYEES, { module }));
  }, [dispatch, module]);

  useEffect(() => {
    dispatch(actions.fetchLeads(module, tableParams, viewType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useComponentDidUpdate(() => {
    if (leadsActionResult.get('success')) {
      dispatch(actions.fetchLeads(module, tableParams, viewType));
    }
  }, [dispatch, leadsActionResult, module, tableParams, viewType]);

  useEffect(() => {
    if (first && !isTableInitialized) {
      const params = makeTableParams(tableFilterSettings, locationSearch);
      const query = makeRequestUrlFromFilter('', params.filters || []);

      setViewTypeData(prevState => ({
        ...prevState,
        [viewType]: {
          ...prevState[viewType],
          locationSearch: query,
          isInitialized: true,
        },
      }));

      navigate(query);
    }
  }, [
    isTableInitialized,
    setViewTypeData,
    viewType,
    first,
    navigate,
    locationSearch,
    tableFilterSettings,
  ]);

  const updateQueryFunction = useCallback(
    query => dispatch(updateQueryParams({ page: QueryPageList.LEADS, query })),
    [dispatch],
  );

  const handleChangeTableProps = useCallback(
    (tableProps: ITableParams): void => {
      dispatch(actions.fetchLeads(module, tableProps, viewType));

      const query = pushQueryToUrl(
        navigate,
        tableProps,
        updateQueryFunction,
        false,
        viewType === 'pipeline',
      );

      setViewTypeData(prevState => {
        if (prevState.table.selected) {
          return { ...prevState, table: { ...prevState.table, locationSearch: query } };
        }

        return { ...prevState, pipeline: { ...prevState.pipeline, locationSearch: query } };
      });
    },
    [dispatch, module, navigate, setViewTypeData, updateQueryFunction, viewType],
  );

  const handleChangeViewType = (view: TViewType): void => {
    setViewTypeData(prevState => ({
      table: {
        ...prevState.table,
        selected: view === 'table',
      },
      pipeline: {
        ...prevState.pipeline,
        selected: view === 'pipeline',
      },
    }));

    const { locationSearch: query } = viewTypeData[view];

    navigate(query);
    updateQueryFunction(query);
  };

  const onAssignSalespersonLeads = useCallback(
    (options: ILeadBulkSalespersonDto) => {
      dispatch(actions.assignSalespersonLeads(options, module));
    },
    [dispatch, module],
  );

  const onChangeLeadsStatus = useCallback(
    options => {
      dispatch(actions.changeLeadsStatus(options, module));
    },
    [dispatch, module],
  );

  const onResetLeadsActionResult = useCallback(() => {
    dispatch(actions.resetLeadsActionResult());
  }, [dispatch]);

  return (
    <RouteBackground>
      {viewPermissions && (
        <LeadsTable
          module={module}
          items={leads}
          totalRows={leadsMeta.get('total')}
          isLoading={isLeadsLoading || isLeadsAssignLoading || isLeadsStatusChangeLoading}
          leadsActionResult={leadsActionResult.toJS()}
          onChangeParams={handleChangeTableProps}
          onAssignSalespersonLeads={onAssignSalespersonLeads}
          onChangeLeadsStatus={onChangeLeadsStatus}
          resetLeadsActionResult={onResetLeadsActionResult}
          tableFilterSettings={tableFilterSettings}
          tableParams={tableParams}
          viewType={viewType}
          setViewType={handleChangeViewType}
        />
      )}
    </RouteBackground>
  );
};

export default Leads;
